import { useCallback, useMemo } from "react";
import Big from "big.js";
import { ListingsSection_AdFragment } from "../../../../codegen/graphql";
import { ExternalListingConfiguration, isVariationExcluded, VariationConfigurationVariation, VariationsConfiguration } from "../../../../common/listingVariationsConfiguration";
import { selectBestSupplierProductVariation } from "../../../../common/selectBestSuplierProductVariation";
import { formatCurrency } from "../../../utils/currency";
import { Toggle } from "../../common/Toggle";
import { TextInput } from "../../common/TextInput";
import { calculatePrices } from "../../../../common/pricesCalculation";
import { SALE_CHANNEL } from "../../../../common/salesChannels";
import PricesCalculationDisplay from "../../common/PricesCalculation_Popover";
import inheritMarginOrTargetPrice from "../../../../common/inheritMarginOrTargetPrice";
import SetMarginOrTargetPrice from "../../common/SetMarginOrTargetPrice";
import { getTranslatedValue } from "../../common/TranslatedValue";



export default function EditListingConfigurationModal_VariationRow({
  adData,
  listing,
  listingConfigurationWithDefaults,
  variation,
  updateVariationConfiguration,
}: {
  adData: ListingsSection_AdFragment;
  listing: ListingsSection_AdFragment['externalListings'][number];
  variation: ListingsSection_AdFragment['variations'][number];
  listingConfigurationWithDefaults: ExternalListingConfiguration;
  updateVariationConfiguration: (variationId: string, updates: Partial<VariationConfigurationVariation>) => void;
}) {
  const variationConfig = listingConfigurationWithDefaults.variationsConfig.variations[variation.id];
  const selectedSupplierProductVariation = selectBestSupplierProductVariation(
    listing.configuration?.supplierKey || '',
    listing.lang,
    variation.supplierProductVariations,
  );

  if (variation.id=== 'ce5b5f13-75c5-483c-83e2-8f02347a7503') {
    console.log('Chrome plaque ');
    console.log(selectedSupplierProductVariation);
  }

  const {
    excluded,
    variationExcluded,
    valueExcluded,
    valuesExcluding,
    typeExcluded,
    typesExcluding,
  } = isVariationExcluded(variation, listingConfigurationWithDefaults);
  const isDisabled = excluded;

  const renderVariationExternalIdForm = useCallback((variationId: string, variationConfig: VariationConfigurationVariation, onChange: (data: any) => void) => {
    return (
      <TextInput
        label="External ID"
        defaultValue={variationConfig.externalId || ''}
        onBlur={(e) => onChange({
          externalId: e.target.value
        })}
      />
    );
  }, []);

  const displayVariationForm = useCallback((variationId: string, variationConfig: VariationConfigurationVariation, onChange: (data: any) => void) => {
    if (
      listing.shop.shopType === 'temu'
      || listing.shop.shopType === 'aliexpress'
    ) {
      return renderVariationExternalIdForm(variationId, variationConfig, onChange);
    }

    return null;
  }, [listing.shop.shopType, renderVariationExternalIdForm]);

  const prices = useMemo(() => {
    if (!selectedSupplierProductVariation) {
      return null;
    }    
    const marginOrTargetPrices = inheritMarginOrTargetPrice({
      listing: listing,
      listingConfiguration: listingConfigurationWithDefaults,
      variation: variation,
      ad: adData,
    });

    const calculatedCost = selectedSupplierProductVariation.calculatedCost.find((cc) => (
      cc.destinationCountry === listing.lang
    ))!;

    return calculatePrices({
      marginPercentOnBaseCost: marginOrTargetPrices.selected.marginPercentOnBaseCost,
      targetListingPriceIncludingVat: marginOrTargetPrices.selected.targetListingPriceIncludingVat,
      listingPriceRoundingMethod: marginOrTargetPrices.selected.listingPriceRoundingMethod,
      productCostExcludingVAT: calculatedCost.totalCostExcludingVAT,
      packingCostExcludingVAT: selectedSupplierProductVariation.packaging?.cost ?? 0,
      forPlatform: listing.shop.shopType as SALE_CHANNEL,
      supplierName: selectedSupplierProductVariation.supplierName,
      origin: 'FR',
      destination: listing.lang,
      packageWeight: selectedSupplierProductVariation.packaging?.weight ?? 0,
      packageSize1: selectedSupplierProductVariation.packaging?.size1 ?? 0,
      packageSize2: selectedSupplierProductVariation.packaging?.size2 ?? 0,
      packageSize3: selectedSupplierProductVariation.packaging?.size3 ?? 0,
    });
  }, [listingConfigurationWithDefaults, selectedSupplierProductVariation, listing]);

  const baseCost = useMemo(() => {
    const calculatedCost = selectedSupplierProductVariation?.calculatedCost.find((cc) => cc.destinationCountry === listing.lang)!;
    return selectedSupplierProductVariation ? new Big(calculatedCost.totalCostExcludingVAT).plus(selectedSupplierProductVariation?.packaging?.cost ?? 0) : new Big(0);
  }, [selectedSupplierProductVariation]);

  return (<>
    {variationExcluded && (
      <p className="col-span-6 text-sm text-orange-700 px-2 pt-2 bg-gray-100">
        This variation is excluded by itself
      </p>
    )}
    {valueExcluded && (
      <p className="col-span-6 text-sm text-orange-700 px-2 pt-2 bg-gray-100">
        This variation is excluded by its type values: {valuesExcluding.map(v => getTranslatedValue(v.value)).join(', ')}
      </p>
    )}
    {typeExcluded && (
      <p className="col-span-6 text-sm text-orange-700 px-2 pt-2 bg-gray-100">
        This variation is excluded by its types: {typesExcluding.map(t => getTranslatedValue(t.type)).join(', ')}
      </p>
    )}
    <div 
      key={variation.id}
      className={`grid gap-2 px-3 py-3 hover:bg-gray-50 ${
        isDisabled ? 'bg-gray-100' : 
        !variationConfig?.include ? 'bg-gray-100' : ''
      }`}
      style={{ 
        position: 'relative',
        gridTemplateColumns: `repeat(${7 + adData.variationTypes.length * 2}, minmax(0, 1fr))`
      }}
    >
      <div className="col-span-1 flex items-center">
        <Toggle
          label=""
          value={variationConfig?.include ?? true}
          onChange={(include) => {
            updateVariationConfiguration(variation.id, { include });
          }}
          disabled={valueExcluded || typeExcluded}
        />
      </div>
      <div className="col-span-1 text-sm text-gray-900">
        <img
          src={variation.variationTypeValues.find(
            v => v.type?.id === adData.typeIdUsedForImages
          )?.medias?.[0]?.url || '/placeholder.png'}
          className="w-20 h-20 object-contain rounded-md"
          alt="Variation"
        />
      </div>
      {adData.variationTypes.map(type => (
        <div
          key={type.id}
          className="col-span-2 text-md text-gray-900"
        >
          {getTranslatedValue(variation.variationTypeValues.find(v => v.type?.id === type.id)?.value)}
        </div>
      ))}
      
      <div className="col-span-2 text-gray-900">
        <SetMarginOrTargetPrice
          data={{
            listing: listing,
            listingConfiguration: listingConfigurationWithDefaults,
            variation: variation,
            ad: adData,
          }}
          setForKey={`listingConfig-variation-${variation.id}`}
          onUpdate={(data) => {
            updateVariationConfiguration(variation.id, data);
          }}
        />
        {prices && <PricesCalculationDisplay prices={prices} />}
      </div>
      <div className="col-span-2 text-gray-900">
        {displayVariationForm?.(
          variation.id,
          variationConfig,
          (data) => updateVariationConfiguration(variation.id, data)
        )}
      </div>
    </div>
  </>);
}